// src/components/lead/useLeadWorkspaceShellProps.tsx
import { useCallback, useEffect, useMemo } from "react";
import { speak } from "../../lib/speech/speak";
import { formatLeadAge } from "../../hooks/useLeadDerivedFields";
import { useLeadWorkspaceGhlSender } from "./useLeadWorkspaceGhlSender";
import { isMergeCandidate } from "@/lib/lead/merge/isMergeCandidate";
import { leadMatchesWorkspaceLocation } from "@/lib/lead/locationScope";
import { PLATFORM_DEFAULT_APP_DISPLAY_NAME } from "@/config/productDisplay";
import { APP_VERSION } from "@/config/version";
import { useOrgSettings } from "@/hooks/useOrgSettings";
import { useLeadGhlSendFlow } from "./useLeadGhlSendFlow";
import { useLeadAsrFlow } from "./useLeadAsrFlow";
import { useLeadAsrStatus } from "./useLeadAsrStatus";
import { useLeadEditorActions } from "./useLeadEditorActions";
import { useLeadMergeConflictController } from "./useLeadMergeConflictController";
import { useLeadWorkspaceViewModel } from "./useLeadWorkspaceViewModel";
import { useLeadWorkspaceState } from "./useLeadWorkspaceState";
import { useLeadWorkspaceOps } from "./useLeadWorkspaceOps";
import { useLeadWorkspaceRuntimeBasics } from "./useLeadWorkspaceRuntimeBasics";
import { useLeadWorkspaceExtractionEffects } from "./useLeadWorkspaceExtractionEffects";
import { useLeadWorkspaceServices } from "./useLeadWorkspaceServices";
import { useCommitTranscriptToCurrentLead } from "./useCommitTranscriptToCurrentLead";
import { useLeadsManagerFilterOptions } from "@/hooks/useLeadsManagerFilterOptions";
import { useLeadWorkspaceFollowUpPolicyRef } from "./useLeadWorkspaceFollowUpPolicyRef";
import { setPendingOpportunityOpen } from "@/lib/opportunity/pendingOpportunityOpen";

export function useLeadWorkspaceShellProps() {
  const orgSettings = useOrgSettings(); // applies org brand CSS vars + provides display values

  const { transcript, setTranscript, leads, setLeads, currentLead, setCurrentLead, salesperson, setSalesperson, location, setLocation, isListening, setIsListening, showProfileEditor, setShowProfileEditor, lastSendAt, setLastSendAt, lastSendStatus, setLastSendStatus, phoneDraft, setPhoneDraft, toast, setToast, emptyLeadAutoDeleteEnabled, setEmptyLeadAutoDeleteEnabled, mergeSuggestion, setMergeSuggestion, locations, setLocations, locationsLoaded, setLocationsLoaded, showAllRecentLeads, setShowAllRecentLeads, showProductDetails, setShowProductDetails, showLocationDetails, setShowLocationDetails, showTranscriptDetails, setShowTranscriptDetails, recentFilter, setRecentFilter, recentSort, setRecentSort, compactView, setCompactView, newTagInput, setNewTagInput, pendingSendLead, setPendingSendLead, pendingLocationSend, setPendingLocationSend, duplicateMatches, setDuplicateMatches, duplicateReason, setDuplicateReason, isStoppingUi, setIsStoppingUi, trainingMode, setTrainingMode, mode, setMode } = useLeadWorkspaceState();

  //Dev Mode
  const DEV_MODE_KEY = "bb-dev-mode";

  // ---------- Queued resend hardening ----------

  // --- Training / Knowledge Mode (V1) ---
  const { auth, scopedOrganizationId, leadWorkspaceStorageKey, viewOrgWideLeads, devMode, devLoginOpen, setDevLoginOpen, devLoginPassword, setDevLoginPassword, devLoginError, devLoginBusy, requestDevMode, disableDevMode, submitDevLogin, getSessionInfo, archiveLead, purgeLeads, syncAllLeads, syncedCount, viableCount, isSyncing, isInitialSyncComplete, selectableLocations, ghlWebhookUrl, refreshLocations, voiceEnabled, voiceProvider, speakMode, speakAnswers, canSpeakHelp, setVoiceEnabled, setVoiceProvider, setSpeakMode, coachIfNeeded, resetCoachState } = useLeadWorkspaceServices({ DEV_MODE_KEY, setTrainingMode, trainingMode, isListening, mode, setMode, currentLead, location, salesperson, locations, locationsLoaded, setSalesperson, setLocation, setLocations, setLocationsLoaded, leads, setLeads, speakFn: speak });

  const { getLeadDefaultFollowUpPolicy } = useLeadWorkspaceFollowUpPolicyRef(scopedOrganizationId);

  const { asrStatus, isAsrReconnecting, handleAsrStatus, onAsrTranscriptChunk, clearAsrStatusUi } =
    useLeadAsrStatus();

  const { leadsRef, locationRef, extractRequestIdRef, extractionRef, listeningRef, currentLeadRef, transcriptRef, asrEngineRef, extract, createLeadsFromBulkTranscript } = useLeadWorkspaceRuntimeBasics({ salesperson, location, leads, currentLead, transcript, toast, setToast, setPhoneDraft, scopedOrganizationId, setLeads, setCurrentLead, setTranscript });

  // Phase 2 transcript merge v2: workspace-level controller for the
  // conflict modal. The controller owns the pending merge state; extraction
  // + re-extract push into it via `handleMergeConflicts`.
  const mergeConflict = useLeadMergeConflictController({
    setLeads,
    setCurrentLead,
    currentLeadRef,
    getActorUserId: () => auth?.session?.user?.id ?? null,
  });

  // If you want it to keep voice-enabled later, remove this.
  useEffect(() => {
    if (trainingMode && isListening) {
      void stopListeningInternal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trainingMode]);

  // Use Effects
  useEffect(() => {
    listeningRef.current = isListening;
  }, [isListening, listeningRef]);

  /** Mirrors `leadMatchesWorkspaceLocation` — see `src/lib/lead/locationScope.ts` for product rules. */
  const locationScopedLeads = useMemo(
    () => leads.filter((l) => leadMatchesWorkspaceLocation(l, location?.id)),
    [leads, location?.id]
  );

  /** Org-wide roles see every synced org lead in Recent; reps stay lot-scoped. */
  const recentLeadsPool = useMemo(
    () => (viewOrgWideLeads ? leads : locationScopedLeads),
    [viewOrgWideLeads, leads, locationScopedLeads],
  );

  const leadsManagerFilters = useLeadsManagerFilterOptions({
    organizationId: scopedOrganizationId,
    viewOrgWideLeads,
    selectableLocations,
  });

  // Slice B3: GHL sender + CRM-link resolver + bulk "Send all" live in
  // a dedicated composition hook so the shell stays under the file-size
  // hygiene cap and future Lead-Control slices have a single place to
  // evolve send behavior.
  const { sendToGHL, isSending, buildGhlPayload, sendAllUnsent } = useLeadWorkspaceGhlSender({
    leads,
    setLeads,
    currentLead,
    setCurrentLead,
    location,
    salesperson,
    webhookUrl: ghlWebhookUrl,
    appDisplayName:
      orgSettings.appDisplayName !== PLATFORM_DEFAULT_APP_DISPLAY_NAME ? orgSettings.appDisplayName : undefined,
    scopedOrganizationId,
    accessToken: auth?.session?.access_token ?? null,
    setToast,
    setLastSendAt,
    setLastSendStatus,
    archiveLead,
    locationScopedLeads,
  });

  const {
    getScopedLeadsPool,
    resolveOrgLocationForOrigin,
    handleSendClick,
    completeGhlSendUsingOriginLocation,
    completeGhlSendUsingWorkspaceLocation,
  } = useLeadGhlSendFlow({
    leadsRef,
    locationRef,
    locations,
    location,
    locationScopedLeads,
    pendingLocationSend,
    setPendingLocationSend,
    setPendingSendLead,
    setDuplicateMatches,
    setDuplicateReason,
    setMergeSuggestion,
    canSpeakHelp,
    speak: canSpeakHelp ? speak : undefined,
    sendToGHL,
  });

  let clearTranscript: (reason: string) => void;
  let startNewLead: () => void;
  let stopListening: () => Promise<void>;
  let closeCurrentLead: () => Promise<void>;
  let continueListening: () => void;

  const {
    findMergeSuggestionForCurrent,
    updateCurrentLeadField,
    mergeLeadIntoCurrent,
    reextractCurrentLead,
    deleteLead,
    openLeadInEditor,
    updateFollowUpStatus,
    createFollowUp,
    saveFollowUp,
  } = useLeadEditorActions({
    getScopedLeadsPool,
    currentLeadRef,
    transcriptRef,
    listeningRef,
    pendingSendLead,
    setPendingSendLead,
    setDuplicateMatches,
    setDuplicateReason,
    setMergeSuggestion,
    setLeads,
    setCurrentLead,
    setTranscript,
    setIsListening,
    extract,
    purgeLeads,
    getActorUserId: () => auth?.session?.user?.id ?? null,
    onMergeConflicts: mergeConflict.handleMergeConflicts,
  });

  const { extraction, stopListeningInternal } = useLeadWorkspaceExtractionEffects({
    extract,
    setTranscript,
    setLeads,
    setCurrentLead,
    setIsListening,
    setMergeSuggestion,
    currentLeadRef,
    transcriptRef,
    listeningRef,
    asrEngineRef,
    extractionRef,
    extractRequestIdRef,
    location,
    resetCoachState,
    clearTranscript: (reason: string) => clearTranscript(reason),
    leads,
    isInitialSyncComplete,
    emptyLeadAutoDeleteEnabled,
    purgeLeads,
    deleteLead,
    getLeadDefaultFollowUpPolicy,
    onMergeConflicts: mergeConflict.handleMergeConflicts,
    handleAsrStatus,
    onAsrTranscriptChunk,
  });

  ({ clearTranscript, startNewLead, stopListening, closeCurrentLead, continueListening } = useLeadAsrFlow({
    asrEngineRef,
    listeningRef,
    currentLeadRef,
    transcriptRef,
    location,
    extraction,
    isStoppingUi,
    emptyLeadAutoDeleteEnabled,
    purgeLeads,
    deleteLead,
    setIsStoppingUi,
    setIsListening,
    setCurrentLead,
    setLeads,
    setTranscript,
    setMergeSuggestion,
    resetCoachState,
    coachIfNeeded,
    findMergeSuggestionForCurrent,
    clearAsrStatusUi,
  }));

  const {
    removeLeadFromWorkspace,
    filteredRecentLeads,
    visibleRecentLeads,
    hasMoreLeads,
    todaysLeads,
    todaysSent,
    todaysUnsent,
    totalUnsent,
    queuedCount,
    leadsDevToolsNode,
  } = useLeadWorkspaceOps({
    recentLeadsPool,
    showAllRecentLeads,
    recentFilter,
    recentSort,
    managerListFilters: leadsManagerFilters.managerListFilters,
    pendingSendLead,
    setPendingSendLead,
    setDuplicateMatches,
    setDuplicateReason,
    setPendingLocationSend,
    setLeads,
    setCurrentLead,
    setTranscript,
    setIsListening,
    setMergeSuggestion,
    currentLeadRef,
    transcriptRef,
    listeningRef,
    leadWorkspaceStorageKey,
    salesperson,
    location,
    leads,
    setSalesperson,
    setLocation,
    appVersion: APP_VERSION,
    asrEngineRef,
  });

  const onLinkedOpportunity = useCallback(
    (opportunityId: string) => {
      const stamp = Date.now();
      setCurrentLead((prev) =>
        prev ? { ...prev, linked_opportunity_id: opportunityId, lastUpdated: stamp } : prev,
      );
      setLeads((prev) =>
        prev.map((l) =>
          l.id === currentLead?.id
            ? { ...l, linked_opportunity_id: opportunityId, lastUpdated: stamp }
            : l,
        ),
      );
    },
    [currentLead?.id, setCurrentLead, setLeads],
  );

  const onViewOpportunity = useCallback(
    (opportunityId: string) => {
      setPendingOpportunityOpen(opportunityId);
      setMode("opportunities");
    },
    [setMode],
  );

  const onOpenSalesIntake = useCallback(() => {
    setMode("salesIntake");
  }, [setMode]);

  const {
    isToday,
    lastUpdatedDisplay,
    derived,
    leadView,
    leadSummaryPayload,
    leadSummaryBuddyTags,
    leadSummaryValidationIssues,
    leadSummaryCanSend,
  } = useLeadWorkspaceViewModel({
    currentLead,
    locationName: location?.name,
    locationAddress: location?.address,
    compactView,
    phoneDraft,
    setPhoneDraft,
    showProductDetails,
    setShowProductDetails,
    showLocationDetails,
    setShowLocationDetails,
    updateCurrentLeadField,
    buildGhlPayload,
    onOpenSalesConsole: () => setMode("salesConsole"),
    createFollowUp,
    onLinkedOpportunity,
    onViewOpportunity,
    setToast,
  });

  const {
    locationDisplay,
    leadSourceDisplay,
    timelineDisplay,
    installAddressDisplay,
    leadStatusClass,
    leadStatusLabel,
    leadAgeDisplay,
  } = derived;

  const commitTranscriptToCurrentLead = useCommitTranscriptToCurrentLead(
    setTranscript,
    setCurrentLead,
    setLeads,
    transcriptRef,
    currentLeadRef
  );

  return { mode, setMode, onOpenSalesIntake, location, toast, setToast, lastSendAt, lastSendStatus, todaysLeads, todaysSent, todaysUnsent, totalUnsent, queuedCount, emptyLeadAutoDeleteEnabled, setEmptyLeadAutoDeleteEnabled, syncedCount, viableCount, isSyncing, syncAllLeads, sendAllUnsent, voiceEnabled, setVoiceEnabled, voiceProvider, setVoiceProvider, speakMode, setSpeakMode, salesperson, setSalesperson, showProfileEditor, setShowProfileEditor, locationsLoaded, locations: selectableLocations, refreshLocations, setLocation, speakAnswers, canSpeakHelp, auth, devMode, disableDevMode, isListening, asrStatus, isAsrReconnecting, transcript, stopListening, isStoppingUi, currentLead, showTranscriptDetails, setShowTranscriptDetails, continueListening, closeCurrentLead, leadView, leads, locationScopedLeads, isToday, locationDisplay, leadSourceDisplay, installAddressDisplay, lastUpdatedDisplay, timelineDisplay, leadStatusClass, leadStatusLabel, leadAgeDisplay, compactView, setCompactView, mergeSuggestion, setMergeSuggestion, mergeLeadIntoCurrent, leadSummaryBuddyTags, leadSummaryPayload, newTagInput, setNewTagInput, updateCurrentLeadField, leadSummaryValidationIssues, leadSummaryCanSend, isSending, handleSendClick, reextractCurrentLead, visibleRecentLeads, filteredRecentLeads, hasMoreLeads, showAllRecentLeads, setShowAllRecentLeads, recentFilter, setRecentFilter, recentSort, setRecentSort, showLeadsManagerFilters: leadsManagerFilters.showLeadsManagerFilters, showLeadsOwnerFilter: leadsManagerFilters.showLeadsOwnerFilter, recentLocationFilter: leadsManagerFilters.recentLocationFilter, setRecentLocationFilter: leadsManagerFilters.setRecentLocationFilter, recentOwnerFilter: leadsManagerFilters.recentOwnerFilter, setRecentOwnerFilter: leadsManagerFilters.setRecentOwnerFilter, ownerCandidates: leadsManagerFilters.ownerCandidates, ownerLabelByUserId: leadsManagerFilters.ownerLabelByUserId, locationNameById: leadsManagerFilters.locationNameById, locationFilterOptions: leadsManagerFilters.locationFilterOptions, openLeadInEditor, deleteLead, isMergeCandidate, formatLeadAge, createLeadsFromBulkTranscript, updateFollowUpStatus, createFollowUp, saveFollowUp, pendingSendLead, pendingLocationSend, setPendingLocationSend, completeGhlSendUsingOriginLocation, completeGhlSendUsingWorkspaceLocation, resolveOrgLocationForOrigin, removeLeadFromWorkspace, duplicateMatches, duplicateReason, sendToGHL, setPendingSendLead, setDuplicateMatches, setDuplicateReason, setCurrentLead, currentLeadRef, transcriptRef, setTranscript, listeningRef, setIsListening, findMergeSuggestionForCurrent, getSessionInfo, requestDevMode, devLoginOpen, devLoginBusy, devLoginPassword, devLoginError, setDevLoginPassword, setDevLoginOpen, submitDevLogin, leadsDevToolsNode, startNewLead, commitTranscriptToCurrentLead, mergeConflict };
}

export type LeadWorkspaceShellModel = ReturnType<typeof useLeadWorkspaceShellProps>;